import React, { useEffect, useState } from 'react';
import { db, functions } from '../firebase';
import { collection, getDocs, query, where, orderBy, limit, startAfter, doc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import toast from 'react-hot-toast';

export const RidesPage = () => {
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, accepted, completed, cancelled
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRide, setSelectedRide] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    fetchRides();
  }, [filter]);

  const fetchRides = async () => {
    setLoading(true);
    try {
      let q = collection(db, 'rides');
      if (filter !== 'all') {
        q = query(collection(db, 'rides'), where('status', '==', filter), orderBy('createdAt', 'desc'), limit(100));
      } else {
        q = query(collection(db, 'rides'), orderBy('createdAt', 'desc'), limit(100));
      }
      const snapshot = await getDocs(q);
      const ridesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setRides(ridesData);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load rides');
    } finally {
      setLoading(false);
    }
  };

  const emergencyCancel = async (rideId) => {
    if (!window.confirm('Emergency cancel this ride? Rider will be notified and refunded if applicable.')) return;
    const reason = prompt('Reason for cancellation:');
    if (!reason) return;
    const cancelFn = httpsCallable(functions, 'emergencyCancelRide');
    try {
      await cancelFn({ rideId, reason });
      toast.success('Ride cancelled');
      fetchRides();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const flagFraud = async (rideId) => {
    const reason = prompt('Reason for fraud flag:');
    if (!reason) return;
    const flagFn = httpsCallable(functions, 'flagRideForFraud');
    try {
      await flagFn({ rideId, reason });
      toast.success('Ride flagged for fraud');
      fetchRides();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const openModal = (ride) => {
    setSelectedRide(ride);
    setModalOpen(true);
  };

  const closeModal = () => {
    setSelectedRide(null);
    setModalOpen(false);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return timestamp.toDate().toLocaleString();
  };

  const filteredRides = rides.filter(ride =>
    searchTerm === '' || ride.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (ride.userId && ride.userId.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (ride.driverId && ride.driverId.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) return <div className="flex justify-center p-8"><div className="text-gray-400">Loading rides...</div></div>;

  return (
    <div className="space-y-6">
      {/* Filters and Search */}
      <div className="bg-gray-800 rounded-lg p-4 flex flex-wrap gap-4 items-center">
        <div>
          <label className="text-sm text-gray-400 mr-2">Filter:</label>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="bg-gray-700 border border-gray-600 rounded px-2 py-1">
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="accepted">Accepted</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <div>
          <input
            type="text"
            placeholder="Search by Ride ID, User ID, Driver ID"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="bg-gray-700 border border-gray-600 rounded px-3 py-1 w-80"
          />
        </div>
        <button onClick={fetchRides} className="bg-emerald-600 hover:bg-emerald-500 px-3 py-1 rounded text-sm">Refresh</button>
      </div>

      {/* Rides Table */}
      <div className="bg-gray-800 rounded-lg overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-700">
            <tr>
              <th className="px-4 py-2 text-left">Ride ID</th>
              <th className="px-4 py-2 text-left">Status</th>
              <th className="px-4 py-2 text-left">User ID</th>
              <th className="px-4 py-2 text-left">Driver ID</th>
              <th className="px-4 py-2 text-left">Fare (R)</th>
              <th className="px-4 py-2 text-left">Payment</th>
              <th className="px-4 py-2 text-left">Created</th>
              <th className="px-4 py-2 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredRides.map(ride => (
              <tr key={ride.id} className="border-t border-gray-700 hover:bg-gray-750 cursor-pointer" onClick={() => openModal(ride)}>
                <td className="px-4 py-2 font-mono text-xs">{ride.id.slice(-8)}</td>
                <td className="px-4 py-2">
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    ride.status === 'completed' ? 'bg-emerald-900 text-emerald-300' :
                    ride.status === 'accepted' ? 'bg-blue-900 text-blue-300' :
                    ride.status === 'cancelled' ? 'bg-red-900 text-red-300' : 'bg-yellow-900 text-yellow-300'
                  }`}>{ride.status}</span>
                </td>
                <td className="px-4 py-2 font-mono text-xs">{ride.userId?.slice(-8)}</td>
                <td className="px-4 py-2 font-mono text-xs">{ride.driverId?.slice(-8) || '-'}</td>
                <td className="px-4 py-2">{((ride.finalFareCents || ride.estimatedFareCents || 0) / 100).toFixed(2)}</td>
                <td className="px-4 py-2">{ride.paymentMethod || '-'}</td>
                <td className="px-4 py-2">{formatDate(ride.createdAt)}</td>
                <td className="px-4 py-2 space-x-2" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => emergencyCancel(ride.id)} className="bg-red-600 hover:bg-red-500 px-2 py-1 rounded text-xs">Cancel</button>
                  <button onClick={() => flagFraud(ride.id)} className="bg-yellow-600 hover:bg-yellow-500 px-2 py-1 rounded text-xs">Flag Fraud</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Ride Details Modal */}
      {modalOpen && selectedRide && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50" onClick={closeModal}>
          <div className="bg-gray-800 rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-semibold mb-4">Ride Details</h3>
            <div className="space-y-2 text-sm">
              <p><strong>Ride ID:</strong> {selectedRide.id}</p>
              <p><strong>Status:</strong> {selectedRide.status}</p>
              <p><strong>User ID:</strong> {selectedRide.userId}</p>
              <p><strong>Driver ID:</strong> {selectedRide.driverId || 'Not assigned'}</p>
              <p><strong>Pickup:</strong> {selectedRide.pickupLocation?.address || `${selectedRide.pickupLocation?.latitude}, ${selectedRide.pickupLocation?.longitude}`}</p>
              <p><strong>Destination:</strong> {selectedRide.destination?.address || 'N/A'}</p>
              <p><strong>Estimated Fare:</strong> R{((selectedRide.estimatedFareCents || 0) / 100).toFixed(2)}</p>
              <p><strong>Final Fare:</strong> R{((selectedRide.finalFareCents || 0) / 100).toFixed(2)}</p>
              <p><strong>Payment Method:</strong> {selectedRide.paymentMethod}</p>
              <p><strong>Payment Reference:</strong> {selectedRide.paymentReference || 'N/A'}</p>
              <p><strong>Created:</strong> {formatDate(selectedRide.createdAt)}</p>
              <p><strong>Completed:</strong> {formatDate(selectedRide.completedAt)}</p>
              <p><strong>Cancelled:</strong> {formatDate(selectedRide.cancelledAt)}</p>
              {selectedRide.flaggedForFraud && <p className="text-red-400"><strong>Fraud Flag:</strong> {selectedRide.fraudReason}</p>}
            </div>
            <div className="flex justify-end mt-6">
              <button onClick={closeModal} className="bg-gray-600 hover:bg-gray-500 px-4 py-2 rounded">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
